import { type ElementType, type FC, type KeyboardEvent, useRef, useState } from 'react';

import {
  autoUpdate,
  FloatingFocusManager,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
} from '@floating-ui/react';
import { EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import cn from 'classnames';

import { POPOVER_PANEL_CLASSES } from '../constants';

export interface ScheduledQueueMenuItem {
  label: string;
  Icon?: ElementType;
  /** Left out, the item only informs, like who can edit the post. */
  onSelect?: () => void;
  isDanger?: boolean;
  isDisabled?: boolean;
}

interface ScheduledQueueMenuProps {
  label: string;
  items: ScheduledQueueMenuItem[];
}

// Opens inside the queue rather than in a portal, so a dialog around the queue keeps the focus and the clicks in it.
export const ScheduledQueueMenu: FC<ScheduledQueueMenuProps> = ({ label, items }) => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const listRef = useRef<Array<HTMLElement | null>>([]);
  const floating = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'bottom-end',
    strategy: 'fixed',
    middleware: [offset(4), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });
  const interactions = useInteractions([
    useClick(floating.context),
    useDismiss(floating.context, { escapeKey: false }),
    useRole(floating.context, { role: 'menu' }),
    useListNavigation(floating.context, { listRef, activeIndex, onNavigate: setActiveIndex, loop: true }),
  ]);

  // Escape closes the menu and stops there, so a dialog around the queue stays open.
  const handleKeyDown = (event: KeyboardEvent) => {
    if (!open || event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    setOpen(false);
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: catches Escape from the trigger and the menu inside it
    <span onKeyDown={handleKeyDown} className="shrink-0">
      <button
        type="button"
        ref={floating.refs.setReference}
        aria-label={label}
        className={cn('flex size-8 items-center justify-center rounded-full text-black/50 transition-colors', {
          'bg-black/5': open,
          'hover:bg-black/5': !open,
        })}
        {...interactions.getReferenceProps()}
      >
        <EllipsisHorizontalIcon className="size-5" aria-hidden="true" />
      </button>
      {open && (
        <FloatingFocusManager context={floating.context} modal={false}>
          <div
            ref={floating.refs.setFloating}
            style={floating.floatingStyles}
            className={cn(POPOVER_PANEL_CLASSES, 'z-[100] min-w-[192px] overflow-hidden py-1 focus:outline-none')}
            {...interactions.getFloatingProps()}
          >
            {items.map(({ label: itemLabel, Icon, onSelect, isDanger, isDisabled }, index) => (
              <button
                key={itemLabel}
                type="button"
                role="menuitem"
                ref={(node) => {
                  listRef.current[index] = node;
                }}
                tabIndex={activeIndex === index ? 0 : -1}
                aria-disabled={!onSelect || isDisabled || undefined}
                className={cn(
                  'flex min-h-10 w-full items-center gap-2 px-4 text-left text-sm font-medium leading-[14px] tracking-[-0.14px] focus:outline-none',
                  {
                    'border-b border-black/10 py-3 text-[13px] font-normal text-black/50': !onSelect && !isDisabled,
                    'hover:bg-black/5 focus-visible:bg-black/5': onSelect && !isDisabled,
                    'cursor-not-allowed text-black opacity-50': isDisabled,
                    'text-red-600': onSelect && isDanger,
                    'text-black': onSelect && !isDanger,
                  },
                )}
                {...interactions.getItemProps({
                  onClick: () => {
                    if (!onSelect || isDisabled) return;
                    setOpen(false);
                    onSelect();
                  },
                })}
              >
                {Icon ? <Icon className="size-4 shrink-0" aria-hidden="true" /> : null}
                {itemLabel}
              </button>
            ))}
          </div>
        </FloatingFocusManager>
      )}
    </span>
  );
};
